import { useState } from "react";
import AxisConfigPageView from "./AxisConfigPageView";
import useEvaApi, { EvaApiCall } from "../../../../hooks/useEvaApi";
import useCustomEffect from "../../../../hooks/useCustomEffect";
import { useParams } from "react-router-dom";
import { useRouteContext } from "../../../../context/RouteContext";

function AxisConfigPage() {
  const callEvaApi = useEvaApi();
  const routeContext = useRouteContext();
  const { axis_config } = useParams();
  const [parkingLocations, setParkingLocations] = useState([]);
  const [submitError, setSubmitError] = useState(null);
  const [configData, setConfigData] = useState({
    name: "",
    url: "",
    username: "",
    password: "",
    auth_method: "basic",
    allow_visitor_plates: true,
    allow_employee_plates: true,
    parking_locations: [],
  });

  /**
   * initialize the component
   */
  useCustomEffect(() => {
    fetchParkingLocations();

    if (axis_config) {
      fetchAxisConfigData();
    }
  });

  /**
   * This method will fetch the Axis config data from the API
   */
  async function fetchAxisConfigData() {
    await callEvaApi(
      new EvaApiCall(`integrations/axis/units/${axis_config}`)
        .setLoadingGroup("axis_configs_page_fetch")
        .setRedirectOnError(true)
        .setOnSuccess((response) => {
          const data = response.data.data;
          if (!data) return;

          setConfigData({
            url: data.url ?? "",
            username: data.username ?? "",
            password: data.password ?? "",
            name: data.name ?? "",
            auth_method: data.auth_method ?? "basic",
            allow_visitor_plates: data.allow_visitor_plates ?? true,
            allow_employee_plates: data.allow_employee_plates ?? true,
            parking_locations: data.parking_locations,
          });
        }),
    );
  }

  /**
   * This method will fetch the parking locations from the API
   */
  async function fetchParkingLocations() {
    await callEvaApi(
      new EvaApiCall("parking_locations")
        .setLoadingGroup("axis_configs_page_fetch")
        .setOnSuccess((response) => {
          setParkingLocations(response.data.data.records);
        }),
    );
  }

  /**
   * This method will update the name on input change
   * @param {event} e
   */
  function onNameChange(e) {
    setConfigData((prev) => ({ ...prev, name: e.target.value }));
  }

  /**
   * This method will update the host on input change
   * @param {event} e
   */
  function onUrlChange(e) {
    setConfigData((prev) => ({ ...prev, url: e.target.value }));
  }

  /**
   * This method will update the username on input change
   * @param {event} e
   */
  function onUsernameChange(e) {
    setConfigData((prev) => ({ ...prev, username: e.target.value }));
  }

  /**
   * This method will update the password on input change
   * @param {event} e
   */
  function onPasswordChange(e) {
    setConfigData((prev) => ({ ...prev, password: e.target.value }));
  }

  /**
   * This method will update the auth method on change
   * @param {string} value
   */
  function onAuthMethodChange(value) {
    setConfigData((prev) => ({ ...prev, auth_method: value }));
  }

  /**
   * This method will update the allow visitor plates on toggle change
   * @param {bool} value
   */
  function onAllowEmployeePlatesChange(value) {
    setConfigData((prev) => ({ ...prev, allow_employee_plates: value }));
  }

  /**
   * This method will update the allow employee plates on toggle change
   * @param {bool} value
   */
  function onAllowVisitorPlatesChange(value) {
    setConfigData((prev) => ({ ...prev, allow_visitor_plates: value }));
  }

  /**
   * This method will update the parking locations on change
   * @param {any} value
   */
  function onParkingLocationsChange(value) {
    setConfigData((prev) => ({ ...prev, parking_locations: value }));
  }

  /**
   * This method will handle the onCancel functionality
   */
  function onCancel() {
    routeContext.back();
  }

  /**e
   * This method will submit the form
   */
  async function onSubmit() {
    await callEvaApi(
      new EvaApiCall(
        axis_config
          ? `integrations/axis/units/${axis_config}`
          : "integrations/axis/units",
      )
        .setLoadingGroup("axis_configs_page_save")
        .setMethod(axis_config ? "PUT" : "POST")
        .setData(configData)
        .setAlertError(true)
        .setErrorState(setSubmitError)
        .setRedirectOnSuccess(true),
    );
  }

  return (
    <AxisConfigPageView
      configData={configData}
      parkingLocations={parkingLocations}
      onCancel={onCancel}
      onNameChange={onNameChange}
      onUrlChange={onUrlChange}
      onUsernameChange={onUsernameChange}
      onPasswordChange={onPasswordChange}
      onAuthMethodChange={onAuthMethodChange}
      onAllowEmployeePlatesChange={onAllowEmployeePlatesChange}
      onAllowVisitorPlatesChange={onAllowVisitorPlatesChange}
      onParkingLocationsChange={onParkingLocationsChange}
      onSubmit={onSubmit}
      submitError={submitError}
    />
  );
}

export default AxisConfigPage;
